import React from 'react';
import classnames from 'classnames';
import { compact } from 'lodash';
import { CheckCircle } from 'react-feather';

import StrapiButton from '../../StrapiLink/StrapiButton';
import { StrapiImageFragment, Maybe, Strapi_ComponentAtomCourseFeatures, Strapi_ComponentAtomLink } from '../../../../graphql.schema';
import { useImageResize } from '../../../hooks/useImageResize';
import Image from '../../Image';

import * as styles from './styles.module.scss';

type HeroCourseProps = {
  name?: string;
  mainHeading?: string | null;
  courseHeadingText?: string;
  image?: Maybe<StrapiImageFragment>;
  short?: string | null;
  courseFeatures?: Maybe<Maybe<Pick<Strapi_ComponentAtomCourseFeatures, 'feature'>>[]>;
  logoOne?: Maybe<StrapiImageFragment>;
  logoTwo?: Maybe<StrapiImageFragment>;
  logoOneUrlLink?: string | null;
  logoTwoUrlLink?: string | null;
  hasRightToLeftLanguage?: boolean;
  courseCTA?: Maybe<Strapi_ComponentAtomLink>
  icon?: 'Arrow' | 'Chevron';
  feature_type?: string;
  courseText?: Maybe<Maybe<Pick<Strapi_ComponentAtomCourseFeatures, 'title' | 'description' >>[]>;
};

export const StrapiHeroCourse = ({
  name,
  image,
  short,
  courseFeatures,
  logoOne,
  logoTwo,
  logoOneUrlLink,
  logoTwoUrlLink,
  mainHeading,
  courseHeadingText,
  hasRightToLeftLanguage,
  courseCTA,
  icon,
  feature_type,
  courseText
}: HeroCourseProps) => {
  const { imageWrapper } = useImageResize(image);
  return (
    <div className={classnames([styles.hero, hasRightToLeftLanguage ? styles.rtlStrapiHeroCourse : ''])}>
      <div>
        <h1 className={classnames([hasRightToLeftLanguage ? styles.rtlcourseName : styles.courseName], 'title brand-a is-1')}>{name ?? mainHeading ?? ''}</h1>
        <div className={classnames('columns', styles.columnReverse)}>
          <div className="column is-offset-1 is-4">
            <p className={classnames('text-1 black-2 mb-6', styles.rtlStrapiHeroDescription)}>{short}</p>
            {logoOne && (
              <div className="is-flex">
                <a target="_blank" rel="noreferrer" href={logoOneUrlLink ?? ''} data-testid='logoOne'>
                  <Image image={logoOne} className={classnames(styles.heroImage, 'mr-2')} loading="lazy" />
                </a>
                <a target="_blank" rel="noreferrer" href={logoTwoUrlLink ?? ''} data-testid='logotwo'>
                  <Image image={logoTwo} className={styles.heroImage} loading="lazy" />
                </a>
              </div>
            )}
            {
              feature_type == 'Course_Bullets' ? (
                <>
                  {courseFeatures && (
                    <>
                      <h5 className={classnames('title brand-a is-5', styles.rtlStrapiHeroShortDescription)} data-testid="heading">
                        {courseHeadingText ?? ''}
                      </h5>
                      <ul>
                        {compact(courseFeatures).map(({ feature }, i) => (
                          <li
                            className={classnames(styles.listItem, 'text-2 black-2', styles.rtlStrapiHeroList)}
                            key={`${feature}-${i}`}
                          >
                            <CheckCircle
                              size={20}
                              className={classnames(styles.check, styles.rtlListIcon, 'black-3 ml-1 has-text-weight-bold')}
                            />{' '}
                            {feature}
                          </li>
                        )
                        )
                        }
                      </ul>
                    </>
                  )}
                </>
              ) : (
                <>
                  {courseText && (
                    <>
                      <ul>
                        {compact(courseText).map(({ title, description }, i) => (
                          <li key={`${title}-${i}`}>
                           <h5 className={classnames('title brand-a is-5', styles.rtlStrapiHeroShortDescription)}>{title}</h5>
                           <div className={classnames(styles.listItem, 'text-2 black-2', styles.rtlStrapiHeroList)}>{description}</div>
                          </li>
                         )
                        )
                        }
                      </ul>
                    </>
                  )}
                </>
              )
            }
            {courseCTA ? <StrapiButton icon={icon} type="primary" {...courseCTA} /> : null}
          </div>
          <div
            ref={imageWrapper}
            className={classnames(
              styles.imageWrapper,
              styles.rtlStrapiHeroImage,
              'column is-offset-1 is-flex is-align-items-center pb-0',
            )}
          >
            <Image image={image} className={styles.heroImage} loading="lazy" />
          </div>
        </div>
      </div>
    </div>
  );
};
